import { useState, useRef, useEffect, useCallback } from "react";
import { streamChat } from "@/lib/api";

let _counter = 0;
const nextId = () => `msg-${++_counter}`;

const WELCOME_MESSAGE = {
  id: nextId(),
  role: "assistant",
  content:
    "Hello — I'm the Hurtler Environmental AI assistant. Ask me about mold, indoor air quality, water damage categories, sampling, or remediation protocols.",
};

export function useChat() {
  const [messages, setMessages] = useState([WELCOME_MESSAGE]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const updateMessage = useCallback((id, updater) => {
    setMessages((prev) => prev.map((m) => (m.id === id ? updater(m) : m)));
  }, []);

  const send = useCallback(
    async (text) => {
      const trimmed = (text ?? "").trim();
      if (!trimmed || streaming) return;

      const assistantId = nextId();
      setMessages((prev) => [
        ...prev,
        { id: nextId(), role: "user", content: trimmed },
        { id: assistantId, role: "assistant", content: "" },
      ]);
      setInput("");
      setStreaming(true);

      await streamChat({
        session_id: sessionId,
        message: trimmed,
        onSession: (sid) => setSessionId(sid),
        onDelta: (delta) =>
          updateMessage(assistantId, (m) => ({ ...m, content: m.content + delta })),
        onError: (err) =>
          updateMessage(assistantId, (m) => ({ ...m, content: `(error: ${err})` })),
        onDone: () => setStreaming(false),
      });
    },
    [sessionId, streaming, updateMessage]
  );

  return { messages, input, setInput, streaming, scrollRef, send };
}
